import EventEmiter from 'events';
import { round } from 'lodash';
import {
  getPortsMsg,
  connectToSerialMsg,
  disconnectSerialMsg,
  armMsg,
  killPythonProcessMsg,
  flightModeMsg,
  requestLogListMsg,
  requestLogMsg,
  deleteAllLogsMsg,
  askForWPListMsg,
  askForWPMsg,
  sendWaypointsMsg,
  sendDroneToWpMsg,
  triggerCameraMsg,
  sendDroneToCoorsMsg,
  calPitotMsg,
  calAccelMsg,
  calAccelNextStepMsg,
  callCompassMsg
} from './telemetry-messages';

/**
 * MessagesEmitted: attitudeData, GPSData, VFRData,
 * HeartBeat, BatStatus, currentWP, distToWp,
 * Unknown MavLink, webServiceClosed, noPorts,
 * ports, portOpen, portClose, logText, itemLogList,
 * logData, logConverted, missionWpCount,
 * downloadedOneWp, downloadedAllWp, uploadedOneWp,
 * uploadedAllWps, pitot, imu, unknown calibration,
 * Unknown Message.
 *
 * messagesSentToTelemetryServer:
 * getPorts, connectSerial,
 * disconnectSerial, arm, killAnyPythonProcess,
 * setFlighMode, requestLogList, requestLog,
 * logErase, askForWpList, askForWp, sendWps,
 * sendDroneToWp, cameraTrigger, sendDroneToCoors,
 * calPitot, calAccel, calAccelNextStep, magical,
 *
 */
class WebSocketTelemetry extends EventEmiter {
  // TODO the url will we passed as a parameter
  constructor(address) {
    super();
    this.webService = new WebSocket(address, 'echo-protocol');
    this.init();
  }

  closeWebService() {
    this.webService.close();
  }

  connectToSerial(toPort, bauds = 115200) {
    // cable = 115200, radio = 57600
    let message = {};
    if (toPort && bauds) {
      message = connectToSerialMsg(bauds, toPort);
    } else {
      message = getPortsMsg;
    }
    this.webService.send(JSON.stringify(message));
  }

  closeSerial() {
    this.webService.send(JSON.stringify(disconnectSerialMsg));
    this.webService.send(JSON.stringify(getPortsMsg));
  }

  arm() {
    this.webService.send(JSON.stringify(armMsg(true)));
  }

  disArm() {
    this.webService.send(JSON.stringify(armMsg(false)));
  }

  killAnyPythonProcess() {
    this.webService.send(JSON.stringify(killPythonProcessMsg));
  }

  setFlightMode(newFlightMode) {
    // FIXME typo in message SUPER TYPO
    this.webService.send(JSON.stringify(flightModeMsg(newFlightMode)));
  }

  requestLogList() {
    this.webService.send(JSON.stringify(requestLogListMsg));
  }

  requestLog(logId, logDir) {
    this.webService.send(JSON.stringify(requestLogMsg(logId, logDir)));
  }

  deleteAllLogs() {
    this.webService.send(JSON.stringify(deleteAllLogsMsg));
  }

  askForWpList() {
    this.webService.send(JSON.stringify(askForWPListMsg));
  }

  askForWp(seqNumber) {
    this.webService.send(JSON.stringify(askForWPMsg(seqNumber)));
  }

  sendWaypointList(waypointTelArray) {
    this.webService.send(JSON.stringify(sendWaypointsMsg(waypointTelArray)));
  }

  sendDroneToWp(waypointNumber) {
    this.webService.send(JSON.stringify(sendDroneToWpMsg(waypointNumber)));
  }

  cameraTrigger() {
    this.webService.send(JSON.stringify(triggerCameraMsg));
  }

  sendDroneToCoors(waypointCoords) {
    this.webService.send(JSON.stringify(sendDroneToCoorsMsg(waypointCoords)));
  }

  calPitot() {
    this.webService.send(JSON.stringify(calPitotMsg));
  }

  calIMU() {
    this.webService.send(JSON.stringify(calAccelMsg));
  }

  nextIMU() {
    this.webService.send(JSON.stringify(calAccelNextStepMsg));
    // Calibration process
    // Place vehicle level and press any key             command=1, result = 1
    // Place vehicle on its LEFT side and press any key  command=1, result = 2
    // Place vehicle on its RIGHT side and press any key command=1, result = 3
    // Place vehicle nose DOWN and press any key         command=1, result = 4
    // Place vehicle nose UP and press any key           command=1, result = 5
    // Place vehicle on its BACK and press any key       command=1, result = 6
    // Calibration successful
  }

  calCompass() {
    this.webService.send(JSON.stringify(callCompassMsg));
  }

  processAttitude(data) {
    // https://mavlink.io/en/messages/common.html#ATTITUDE
    let pitchRead = data.pitch;
    let rollRead = data.roll;
    let yawRead = data.yaw;
    pitchRead = (pitchRead * 180) / Math.PI; // degrees (-180,180)
    rollRead = (rollRead * 180) / Math.PI; // degrees (-180,180)
    yawRead = (yawRead * 180) / Math.PI; // degrees (-180,180)
    this.emit('attitudeData', { pitchRead, rollRead, yawRead });
  }

  getGPSSignalType(type) {
    switch (Number(type)) {
      case 0:
        return 'No GPS';
      case 1:
        return 'No Position';
      case 2:
        return '2D';
      case 3:
        return '3D';
      case 4:
        return 'DGPS 3D';
      case 5:
        return 'RTK Float 3D';
      case 6:
        return 'RTK Fixed 3D';
      case 7:
        return 'Static';
      case 8:
        return 'PPP 3D';
      default:
        return 'No GPS';
    }
  }

  processGPS(data) {
    // https://mavlink.io/en/messages/common.html#GPS_RAW_INT
    // https://mavlink.io/en/messages/common.html#GPS_FIX_TYPE
    let latRead = data.lat;
    let lonRead = data.lon;
    let altRead = data.alt;
    let numberOfSatellites = data.satellites_visible;
    let courseOverGround = data.cog;
    const signalType = this.getGPSSignalType(data.fix_type); // importante para el tipo de recepción (no gps, 3dFix, etc)
    let speedRead = data.vel;

    if (numberOfSatellites === 255) {
      numberOfSatellites = 0;
    }
    altRead /= 1000; // AMSL in m
    lonRead /= 10000000; // degrees
    latRead /= 10000000; // degrees
    courseOverGround /= 100; // degrees // degrees (0,359.99)
    speedRead /= 100; // GPS Ground Speed in m/s
    this.emit('GPSData', {
      altRead,
      lonRead,
      latRead,
      signalType,
      speedRead,
      courseOverGround,
      numberOfSatellites
    });
  }

  processVFR(data) {
    // https://mavlink.io/en/messages/common.html#VFR_HUD
    const { airspeed } = data;
    this.emit('VFRData', { airspeed });
  }

  processHeartbeat(data) {
    // http://mavlink.org/messages/common#HEARTBEAT
    let mode = 'disarmed';
    // TODO this does not work yet
    if (data.base_mode === 81 || data.base_mode === 89) {
      mode = 'disarmed';
    } else if (data.custom_mode === 5 && data.base_mode === 209) {
      mode = 'manual';
    } else if (data.custom_mode === 10 && data.base_mode === 217) {
      mode = 'auto';
    } else if (data.custom_mode === 15 && data.base_mode === 217) {
      mode = 'guided';
    } else if (data.custom_mode === 11 && data.base_mode === 217) {
      mode = 'rtl';
    } else if (data.custom_mode === 458752) {
      mode = 'kstabilized';
    } else if (data.custom_mode === 196608) {
      mode = 'kpoistion';
    } else if (data.custom_mode === 67371008) {
      mode = 'kmission';
    } else if (data.custom_mode === 50593792) {
      mode = 'khold';
    } else if (data.custom_mode === 84148224) {
      mode = 'krtl';
    } else {
      mode = `unknown ${data.base_mode}`;
    }
    console.log(JSON.stringify({ mode, custom: data.custom_mode, base: data.base_mode })); // eslint-disable-line
    this.emit('HeartBeat', { mode });
  }

  processSysStatus(data) {
    // http://mavlink.org/messages/common#SYS_STATUS
    const voltage = round(data.voltage_battery / 1000, 1);
    const remaining = data.battery_remaining;
    const amper = round(data.current_battery / 1000, 1);
    this.emit('BatStatus', { voltage, remaining, amper });
  }

  processCurrentWP(data) {
    // https://mavlink.io/en/messages/common.html#MISSION_CURRENT
    const lastWp = data.seq;
    this.emit('currentWP', { lastWp });
  }

  processNavController(data) {
    // https://mavlink.io/en/messages/common.html#NAV_CONTROLLER_OUTPUT
    const distToWp = data.wp_dist;
    this.emit('distToWp', { distToWp });
  }

  manageMavlink(data) {
    switch (data.parameter) {
      case 'ATTITUDE':
        this.processAttitude(data);
        break;
      case 'GPS_RAW_INT':
        this.processGPS(data);
        break;
      case 'VFR_HUD':
        this.processVFR(data);
        break;
      case 'HEARTBEAT':
        this.processHeartbeat(data);
        break;
      case 'SYS_STATUS':
        this.processSysStatus(data);
        break;
      case 'MISSION_CURRENT':
        this.processCurrentWP(data);
        break;
      case 'NAV_CONTROLLER_OUTPUT':
        this.processNavController(data);
        break;
      default:
        console.warn(data);
        this.emit('Unknown MavLink', data);
        throw new Error('Unknown MavLink', data);
    }
  }

  init() {
    this.webService.onerror = () => {
      this.emit('WebSocketConn', false);
      this.emit('SerialConn', false);
      console.warn('Connection Error');
    };

    this.webService.onopen = () => {
      this.emit('WebSocketConn', true);
      console.warn('WebSocket Client Connected');
      this.webService.send(JSON.stringify(getPortsMsg));
    };

    this.webService.onclose = () => {
      this.emit('WebSocketConn', false);
      this.emit('SerialConn', false);
      console.warn('webService Closed');
    };

    this.webService.onmessage = e => {
      try {
        const msg = JSON.parse(e.data);
        const messageType = msg.type;
        // if (messageType !== 'mavlink') {
        //     console.log(e);
        // }
        if (messageType) {
          switch (messageType) {
            case 'ports':
              // Message Format for ports, Response from getPorts
              // msg = {
              //   type: 'ports',
              //   serialOpen: true/false,
              //   ports: { type: 'ports', ports: [{ portName: 'name' },...] },
              // };
              if (msg.ports.serialOpen) {
                this.emit('SerialConn', true);
                this.emit('ports', msg);
                this.emit('portOpen', msg);
              } else {
                this.emit('SerialConn', false);
                const { ports } = msg.ports;
                if (msg.ports.type === 'ports') {
                  if (ports.length === 0) {
                    this.emit('noPorts', msg);
                    throw new Error('There are no ports to connect', msg);
                  } else {
                    this.emit('ports', msg);
                  }
                }
              }
              break;
            case 'portOpen':
              // Message Format for portOpen, Response from connectSerial
              // This message is only sent when opening the port for the first time
              // If you reload the page and the port is open this message is not sent
              // msg = {
              //   type: 'portOpen',
              // };
              this.emit('SerialConn', true);
              this.emit('portOpen', msg);
              break;
            case 'portClose':
              // Message Format for portClose, Response from disconnectserial
              // This message is only sent when closing the port
              // msg = {
              //   type: 'portClose',
              // };
              this.emit('SerialConn', false);
              this.emit('portClose', msg);
              break;
            case 'connStatus':
              // Message Format for connStatus, Response from connectSerial
              // msg = {
              //   type: 'connStatus',
              //   status: true/false,
              // };
              break;
            case 'mavlink':
              this.manageMavlink(msg.data);
              break;
            case 'message':
              this.emit('logText', msg.msg);
              break;
            case 'itemLogList':
              // Message Format for itemLogList
              // msg = {
              //   type: 'itemLogList',
              //   id: logId,
              //   numlogs: total number of logs,
              //   MbSize: size in Mb of this log,
              //   ofset: number of Mb downloaded,
              // };
              this.emit('itemLogList', msg);
              break;
            case 'logData':
              // Message Format for logData
              // msg = {
              //   type: 'logData',
              //   id: logId,
              //   ofset: number of Mb downloaded,
              // };
              this.emit('logData', msg);
              break;
            case 'logConverted':
              // TODO
              // Message Format for logConverted
              // msg = {
              //   type: 'logConverted',
              //   id: logId,
              // };
              this.emit('logConverted', msg);
              break;
            case 'missionWpCount':
              // http://mavlink.org/messages/common#MISSION_COUNT
              // Message format for missionWpCount
              // msg = {
              //   type: 'missionWpCount',
              //   data: {
              //     mission_type: Mission type, see MAV_MISSION_TYPE,
              //     parameter: 'MISSION_COUNT',
              //     count: Number of mission items in the sequence
              //     target_component: Component ID,
              //     target_system: System ID,
              //   },
              // };
              this.emit('missionWpCount', msg.data.count);
              break;
            case 'downloadedOneWp':
              // http://mavlink.org/messages/common#MISSION_ITEM_INT
              // Message format for downloadedOneWp
              // msg = {
              //   data: {
              //     autocontinue: autocontinue to next wp,
              //     command: The scheduled action for the waypoint, as defined by MAV_CMD enum,
              //     current: false:0, true:1,
              //     frame: The coordinate system of the waypoint, as defined by MAV_FRAME enum,
              //     mission_type: Mission type, see MAV_MISSION_TYPE,
              //     param1: PARAM1, see MAV_CMD enum,
              //             Minimum pitch (if airspeed sensor present), desired pitch without sensor
              //     param2: PARAM2, see MAV_CMD enum,
              //     param3: PARAM3, see MAV_CMD enum,
              //     param4: PARAM4, see MAV_CMD enum,
              //     parameter: 'MISSION_ITEM_INT',
              //     seq: number of waypoint in sequence,
              //     target_component: Component ID,
              //     target_system: System ID,
              //     x: PARAM5 / local: x position, global: latitude,
              //     y: PARAM6 / y position: global: longitude,
              //     z: PARAM7 / z position: global: altitude (relative or absolute, depending on frame.,
              //   },
              //   type: 'downloadedOneWp'
              // }
              this.emit('downloadedOneWp', msg);
              break;
            case 'downloadedAllWp':
              // Message format for downloadedAllWp
              // msg = {
              //   data: total waypoints downloaded,
              //   type: 'downloadedAllWp'
              // };
              this.emit('downloadedAllWp', msg);
              break;
            case 'uploadedOneWp':
              // http://mavlink.org/messages/common#MISSION_REQUEST
              // Message format for uploadedOneWp
              // msg = {
              //   type: 'uploadedOneWp',
              //   data: {
              //     mission_type: Mission type, see MAV_MISSION_TYPE,
              //     parameter: 'MISSION_REQUEST',
              //     seq: seqNumber, // waypoint number in sequence
              //     target_component: Component ID,
              //     target_system: System ID,
              //   },
              // };
              this.emit('uploadedOneWp', msg);
              break;
            case 'uploadedAllWps':
              // Message format for uploadedAllWps
              // msg = {
              //   type: 'uploadedAllWps',
              //   data: {
              //     totalDownloaded: number of waypoints downloaded,
              //     totalInAutopilot: number of waypoints in autopilot,
              //     tries: number of tries ,
              //   },
              // };
              this.emit('uploadedAllWps', msg);
              break;
            case 'calibration':
              // Message Format for calibration
              // TODO
              // console.log(msg);
              switch (msg.data.type) {
                case 'pitot':
                  this.emit('pitot', msg);
                  break;
                case 'accel':
                  this.emit('imu', msg);
                  break;
                default:
                  this.emit('unknown calibration', msg);
              }
              break;
            default:
              console.warn('Unknown Message from telemetry server');
              console.warn(msg);
              this.emit('Unknown Message', msg);
              throw new Error('This message is not defined', msg);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
  }
}

export default WebSocketTelemetry;
